import React, { useEffect, useState, useContext } from 'react';
import classNames from 'classnames';
import { renderToString } from 'react-dom/server';
import PropTypes from 'prop-types';
import styles from './Map.module.scss';
import GoogleMapReact from 'google-map-react';
import BaseContext from '../../containers/BasePage/BaseContext';
import CookieOverlay from '../CookieOverlay';
import MapInfoWindow from '../MapInfoWindow';
import { CookieStateContext } from '../../containers/BasePage/state';
import { useTranslation } from 'next-i18next';

const createMapOptions = () => {
    return {
        clickableIcons: false,
        clickableLabels: false,
        fullscreenControl: false,
        maxZoom: 17,
    };
};

const Map = ({
    apiKey,
    items = [],
    zoomLevel = 17,
    untranslatedModifiers = [],
}) => {
    const { googleMapsApiKey } = useContext(BaseContext);
    const mapApiKey = apiKey || googleMapsApiKey;

    const { t } = useTranslation('common');

    const [map, setMap] = useState(null);
    const [maps, setMaps] = useState(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const [mapBounds, setMapBounds] = useState(null);
    const [infoWindow, setInfoWindow] = useState(null);
    const [showMapWithoutCookieConsent, setShowMapWithoutCookieConsent] =
        useState(false);

    const { state } = useContext(CookieStateContext) || {};
    const cookieConsent = state?.categories?.includes('targeting');

    const itemGroups =
        items && items.length > 0
            ? items.reduce((acc, item) => {
                  const key = item?.type === 'route' ? 'routes' : 'pois';
                  if (!acc[key]) {
                      acc[key] = [];
                  }
                  acc[key].push(item);
                  return acc;
              }, {})
            : {};

    const pois = itemGroups?.pois || [];
    const routes = itemGroups?.routes || [];
    const readMoreText = t('map.readMore');

    const triggerInfoWindow = ({ lat, lng, text, address, href, readMore }) => {
        infoWindow.setPosition({ lat: parseFloat(lat), lng: parseFloat(lng) });
        infoWindow.setContent(
            renderToString(
                <MapInfoWindow
                    title={text || address}
                    href={href}
                    readMore={readMore || readMoreText}
                />
            )
        );
        infoWindow.open(map);
    };

    useEffect(() => {
        if (infoWindow && items.length === 1) {
            const item = items[0];
            triggerInfoWindow(item);
        }
    }, [infoWindow]);

    useEffect(() => {
        if (!maps || !map) return;

        if (pois.length === 1 && routes.length <= 0) {
            /* Center map on single point */
            const center = new maps.LatLng(items[0].lat, items[0].lng);
            map.setCenter(center);
            setIsMapReady(true);
        } else if (items.length >= 1) {
            let bounds = new maps.LatLngBounds();
            let processedRoutesCount = 0;
            let kmlLayers = [];

            items.forEach(({ lat, lng }) => {
                bounds.extend(
                    new maps.LatLng(parseFloat(lat), parseFloat(lng))
                );
            });

            if (routes.length > 0) {
                // Load each KML layer and check if it's loaded
                routes.forEach((item) => {
                    const routeTitle = item.text || item.address;
                    const kmlLayer = new maps.KmlLayer({
                        url: `https://www.google.com/maps/d/kml?mid=${item.route}`,
                        map: map,
                        title: routeTitle,
                        suppressInfoWindows: true, // Disable default info window
                    });
                    kmlLayers.push(kmlLayer);

                    maps.event.addListener(kmlLayer, 'click', () => {
                        triggerInfoWindow(item);
                    });

                    // Listen for when metadata (including viewport) is ready
                    maps.event.addListenerOnce(
                        kmlLayer,
                        'metadata_changed',
                        () => {
                            if (kmlLayer.getDefaultViewport()) {
                                bounds.union(kmlLayer.getDefaultViewport());
                            }
                            processedRoutesCount++;

                            // If all routes are processed (successfully or failed), finalize bounds
                            if (processedRoutesCount === routes.length) {
                                setMapBounds(bounds);
                                setIsMapReady(true);
                            }
                        }
                    );

                    // Handle KML layer errors explicitly
                    maps.event.addListener(kmlLayer, 'status_changed', () => {
                        if (kmlLayer.getStatus() !== maps.KmlLayerStatus.OK) {
                            console.error(
                                `Failed to load route: ${routeTitle}`
                            );
                            processedRoutesCount++; // Count failed loads too

                            if (processedRoutesCount === routes.length) {
                                setMapBounds(bounds);
                                setIsMapReady(true);
                            }
                        }
                    });
                });
            } else {
                // If no routes, fit bounds with only item positions
                setMapBounds(bounds);
                setIsMapReady(true);
            }
        }
    }, [maps, map]);

    // Fit map bounds when ready
    useEffect(() => {
        if (isMapReady && mapBounds) {
            map.fitBounds(mapBounds);
        }
    }, [isMapReady, mapBounds]);

    const center = {
        lat: parseFloat(60.95),
        lng: parseFloat(20.33),
    };

    const classes = classNames(
        styles['Map'],
        'Map',
        untranslatedModifiers.map((x) => styles[x])
    );

    return (
        <div className={classes}>
            <div className={styles['Map__Container']}>
                {!(cookieConsent || showMapWithoutCookieConsent) && (
                    <CookieOverlay
                        text={t('map.cookieText')}
                        buttonText={t('map.cookieButton')}
                        modifiers={['Map']}
                        handleClick={() => {
                            setShowMapWithoutCookieConsent(true);
                        }}
                    />
                )}
                {(cookieConsent || showMapWithoutCookieConsent) && (
                    <GoogleMapReact
                        onClick={() => {
                            if (infoWindow) {
                                infoWindow.close();
                            }
                        }}
                        defaultCenter={center}
                        options={createMapOptions}
                        bootstrapURLKeys={{ key: mapApiKey }}
                        yesIWantToUseGoogleMapApiInternals
                        tabIndex={-1}
                        onGoogleApiLoaded={({ map, maps }) => {
                            setMap(map);
                            setMaps(maps);
                            setInfoWindow(new maps.InfoWindow());
                        }}
                        defaultZoom={zoomLevel}>
                        {pois.map((item, i) => (
                            <Pin
                                key={i}
                                onClick={() => triggerInfoWindow(item)}
                                {...item}
                            />
                        ))}
                    </GoogleMapReact>
                )}
            </div>
        </div>
    );
};

Map.propTypes = {
    items: PropTypes.array.isRequired,
    apiKey: PropTypes.string.isRequired,
    zoomLevel: PropTypes.number,
    untranslatedModifiers: PropTypes.arrayOf(PropTypes.string),
};

const Pin = ({ lng, lat, onClick }) => {
    return (
        <button
            onClick={onClick}
            lat={parseFloat(lat)}
            lng={parseFloat(lng)}
            className={styles['Map__Poi']}
        />
    );
};

Pin.propTypes = {
    map: PropTypes.object,
    lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    href: PropTypes.string,
};

export default Map;
